import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/auth/authContext';
import TeamContext from '../../context/teams/teamContext';
import UnifiedMatchContext from '../../context/matches/UnifiedMatchContext';
import MatchGroupContext from '../../context/matchGroups/matchGroupContext';
import MatchItemVGC from '../matches/MatchItemVGC';
import MatchItemTCG from '../matches/MatchItemTCG';
import MatchGroups from '../matches/MatchGroups';
import MatchGroupModal from '../matches/MatchGroupModal';
import AnnouncementModal from '../layout/AnnouncementModal';
import UsernamePromptModal from '../layout/UsernamePromptModal';
import DeckContext from '../../context/decks/deckContext';
import SeasonContext from '../../context/seasons/seasonContext';
import MatchCounterTicker from '../dashboard/MatchCounterTicker';
import Pagination from '../layout/Pagination';
import TeamNameWithTooltip from '../shared/TeamNameWithTooltip';
import PokemonSprite from '../shared/PokemonSprite';
import { exportToCSV } from '../../utils/exportUtils';
import { getDeckName } from '../../utils/deckUtils';
import '../../styles/matches.css';
import './Dashboard.css';
import './DashboardModern.css';
import '../matches/ModernMatchList.css';

const Dashboard = () => {
  const [useModernUI] = useState(true); // Toggle for modern UI
  const navigate = useNavigate();
  const location = useLocation();
  const [showAllTimeStats, setShowAllTimeStats] = useState(false);
  
  // Generate Pokemon-themed name for teams without names
  const generatePokemonThemedName = (team) => {
    const themes = [
      "Elite Squad", "Champions", "Masters", "Legends", "Heroes",
      "Victory Brigade", "Battle Team", "Premier League", "All-Stars",
      "Dream Team", "Power Players", "Ultimate Six", "Battle Ready"
    ];
    
    // Use team composition to generate a consistent name
    if (!team) return themes[0];
    
    const teamStr = Array.isArray(team) ? team.join('') : String(team);
    let hash = 0;
    for (let i = 0; i < teamStr.length; i++) {
      hash = ((hash << 5) - hash) + teamStr.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    const index = Math.abs(hash) % themes.length;
    return themes[index];
  };
  const { loadUser, user } = useContext(AuthContext);
  const { getTeams, teams } = useContext(TeamContext);
  const { decks, getDecks } = useContext(DeckContext);
  const { currentSeason, getCurrentSeason, getSeasonStats, seasonStats } = useContext(SeasonContext);
  const { 
    tcgMatches, 
    vgcMatches, 
    getTCGMatches, 
    getVGCMatches, 
    deleteTCGMatch, 
    deleteVGCMatch,
    loading 
  } = useContext(UnifiedMatchContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  
  // Check user game preferences
  const showTCG = user?.gamePreferences ? user.gamePreferences.tcg === true : true;
  const showVGC = user?.gamePreferences ? user.gamePreferences.vgc === true : true;
  const showBothGames = showTCG && showVGC;
  
  // Set default tab based on preferences
  const getDefaultTab = () => {
    if (!showBothGames) {
      return showTCG ? 'TCG' : 'VGC';
    }
    return location.state?.activeTab || localStorage.getItem('dashboardActiveTab') || 'TCG';
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [dashboardView, setDashboardView] = useState(localStorage.getItem('dashboardView') || 'matches');
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [matchesPerPage] = useState(15); // Display 15 matches per page
  const [teamFilter, setTeamFilter] = useState(location.state?.filterTeam || null);
  const [teamFilterName, setTeamFilterName] = useState(location.state?.teamName || null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [showLogChooser, setShowLogChooser] = useState(false);
  // Collapsed by default — only affects mobile (CSS keeps it open on desktop).
  const [analyticsCollapsed, setAnalyticsCollapsed] = useState(true);

  // Use ref to prevent unnecessary re-renders when switching tabs
  const previousView = React.useRef(localStorage.getItem('dashboardView') || 'matches');
  // Use ref to track if initial data load has been done
  const initialDataLoadedRef = React.useRef(false);
  
  // Force correct tab when only one game is selected
  React.useEffect(() => {
    if (!showBothGames) {
      const correctTab = showTCG ? 'TCG' : 'VGC';
      if (activeTab !== correctTab) {
        setActiveTab(correctTab);
        localStorage.setItem('dashboardActiveTab', correctTab);
      }
    }
  }, [showTCG, showVGC, showBothGames, activeTab]);

  useEffect(() => {
    // Get teams, decks, and current season if authenticated
    if (user && !initialDataLoadedRef.current) {
      getTeams();
      getCurrentSeason();
      // Fetch matches only once on initial mount
      getTCGMatches();
      getVGCMatches();
      if (activeTab === 'TCG') {
        getDecks();
      }
      initialDataLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Ensure matches are loaded when switching tabs if they haven't been loaded yet
  useEffect(() => {
    if (user && initialDataLoadedRef.current) {
      // Always ensure we have the latest matches when viewing the dashboard
      if (tcgMatches.length === 0 && vgcMatches.length === 0) {
        getTCGMatches();
        getVGCMatches();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  // Handle tab changes separately to prevent refresh loops
  useEffect(() => {
    if (user && activeTab === 'TCG') {
      getDecks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Get season stats when current season changes
  useEffect(() => {
    if (currentSeason && user) {
      getSeasonStats(currentSeason._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSeason]);
  
  // Reset to first page when search term or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, selectedTags]);

  // Fetch available tags for filtering
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get('/api/match-tags/popular', {
          params: { limit: 50 }
        });
        setAvailableTags(response.data || []);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setAvailableTags([]); // Set to empty array on error
      }
    };

    if (user) {
      fetchTags();
    }
  }, [user]);

  // Show announcement modal on first dashboard visit
  useEffect(() => {
    const hasSeenAnnouncement = localStorage.getItem('hasSeenFeatureAnnouncement');
    if (!hasSeenAnnouncement && user) {
      // Small delay to let dashboard load first
      const timer = setTimeout(() => {
        setShowAnnouncement(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleCloseAnnouncement = () => {
    setShowAnnouncement(false);
    localStorage.setItem('hasSeenFeatureAnnouncement', 'true');
  };

  // Show username prompt if user hasn't set usernames and hasn't dismissed the prompt
  useEffect(() => {
    if (user && !user.usernamePromptDismissed) {
      const hasUsernames = user.ptcglUsername || user.limitlessUsername;
      if (!hasUsernames) {
        // Small delay to let dashboard load first, and show after announcement
        const timer = setTimeout(() => {
          setShowUsernamePrompt(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const handleSaveUsernames = async ({ ptcglUsername, limitlessUsername }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };

      const body = JSON.stringify({ ptcglUsername, limitlessUsername });
      const res = await axios.put('/api/auth/update-user', body, config);

      // Update user in context
      loadUser();
      setShowUsernamePrompt(false);
    } catch (err) {
      console.error('Error saving usernames:', err);
    }
  };

  const handleDismissUsernamePrompt = async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };

      await axios.put('/api/auth/dismiss-username-prompt', {}, config);

      // Update user in context
      loadUser();
      setShowUsernamePrompt(false);
    } catch (err) {
      console.error('Error dismissing username prompt:', err);
    }
  };

  const handleLogMatchClick = () => {
    // Open a chooser asking how to log: manual / import PTCGL / import Limitless
    setShowLogChooser(true);
  };
  
  const handleCreateGroupClick = () => {
    setIsGroupModalOpen(true);
  };
  
  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
  };

  // Function to safely process matches with proper type checking
  const processTCGMatches = () => {
    // First check if tcgMatches exists and is an array
    if (!tcgMatches) return [];
    if (!Array.isArray(tcgMatches)) return [];
    
    // Then safely map over the array
    return tcgMatches.map(match => ({ ...match, gameType: 'TCG' }));
  };
  
  const processVGCMatches = () => {
    // Only use the new VGC match model
    const newVGCMatches = (!vgcMatches) ? [] : 
      !Array.isArray(vgcMatches) ? [] : 
      vgcMatches.map(match => ({ ...match, gameType: 'VGC' }));
    
    return newVGCMatches;
  };
  
  // Combine regular matches and TCG matches with enhanced safety
  const allMatches = [
    ...processVGCMatches(),
    ...processTCGMatches()
  ];
  
  // Ensure allMatches is an array before filtering
  const filteredMatches = Array.isArray(allMatches) ? allMatches.filter(match => {
    if (!match) return false;
    if (activeTab === 'TCG' && match.gameType === 'VGC') return false;
    if (activeTab === 'VGC' && match.gameType === 'TCG') return false;
    
    // Apply team filter if present (for VGC matches)
    if (teamFilter && match.gameType === 'VGC') {
      if (!match.playerTeam) return false;
      // Ensure playerTeam is an array
      const playerTeamArray = Array.isArray(match.playerTeam) 
        ? match.playerTeam 
        : (typeof match.playerTeam === 'string' ? match.playerTeam.split(',') : []);
      
      // Normalize team names for comparison (remove forms, lowercase, trim)
      const normalizeTeamName = (name) => name.toLowerCase().trim().split('-')[0];
      
      const matchTeamSignature = playerTeamArray
        .map(normalizeTeamName)
        .sort()
        .join(',');
      
      const filterTeamSignature = teamFilter
        .split(',')
        .map(normalizeTeamName)
        .sort()
        .join(',');
        
      if (matchTeamSignature !== filterTeamSignature) return false;
    }

    // Apply tag filter if tags are selected
    if (selectedTags.length > 0) {
      if (!match.tags || match.tags.length === 0) return false;
      // Match if any selected tag is in match.tags
      const hasMatchingTag = selectedTags.some(selectedTag =>
        match.tags.some(matchTag => matchTag.toLowerCase() === selectedTag.toLowerCase())
      );
      if (!hasMatchingTag) return false;
    }

    // Different search logic based on match type
    if (match.gameType === 'VGC') {
      return (
        match.opponentName?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
        match.notes?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
        match.format?.toLowerCase().includes(searchTerm?.toLowerCase() || '')
      );
    } else {
      return (
        match.opponentDeck?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
        match.notes?.toLowerCase().includes(searchTerm?.toLowerCase() || '')
      );
    }
  }) : [];

  // Safe sorting with defensive checks
  const sortedMatches = Array.isArray(filteredMatches) ? [...filteredMatches].sort((a, b) => {
    if (!a || !b) return 0;
    switch (sortOption) {
      case 'newest': return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
      case 'oldest': return new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
      case 'a-z':    
        // For VGC matches, use opponentName; for TCG matches, use opponentDeck
        if (a.gameType === 'VGC' && b.gameType === 'VGC') {
          return (a?.opponentName || '').localeCompare(b?.opponentName || '');
        } else if (a.gameType === 'TCG' && b.gameType === 'TCG') {
          return (a?.opponentDeck || '').localeCompare(b?.opponentDeck || '');
        } else {
          // If mixing match types, sort by gameType first
          return a.gameType.localeCompare(b.gameType);
        }
      case 'z-a':
        // For VGC matches, use opponentName; for TCG matches, use opponentDeck
        if (a.gameType === 'VGC' && b.gameType === 'VGC') {
          return (b?.opponentName || '').localeCompare(a?.opponentName || '');
        } else if (a.gameType === 'TCG' && b.gameType === 'TCG') {
          return (b?.opponentDeck || '').localeCompare(a?.opponentDeck || '');
        } else {
          // If mixing match types, sort by gameType first
          return b.gameType.localeCompare(a.gameType);
        }
      default:       return 0;
    }
  }) : [];

  // Calculate performance metrics (use unfiltered matches for analysis)
  const calculatePerformanceMetrics = () => {
    let activeMatches = allMatches.filter(match => match.gameType === activeTab);
    
    // Filter by current season if available
    if (currentSeason) {
      activeMatches = activeMatches.filter(match => {
        const matchDate = new Date(match.createdAt);
        return matchDate >= new Date(currentSeason.startDate) && 
               matchDate <= new Date(currentSeason.endDate);
      });
    }
    
    // Do not fallback to all-time when in a season view; show 0 for this season
    
    if (activeMatches.length === 0) return null;

    const wins = activeMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length;

    const losses = activeMatches.length - wins;
    const winRate = ((wins / activeMatches.length) * 100).toFixed(1);

    // Calculate trend (last 10 matches vs previous 10)
    const recentMatches = activeMatches.slice(0, 10);
    const previousMatches = activeMatches.slice(10, 20);
    
    const recentWins = recentMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length;

    const previousWins = previousMatches.length > 0 ? previousMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length : 0;

    const recentWinRate = recentMatches.length > 0 ? (recentWins / recentMatches.length) * 100 : 0;
    const previousWinRate = previousMatches.length > 0 ? (previousWins / previousMatches.length) * 100 : 0;
    const trend = recentWinRate - previousWinRate;

    return {
      totalMatches: activeMatches.length,
      wins,
      losses,
      winRate,
      recentWinRate: Math.round(recentWinRate),
      recentCount: recentMatches.length,
      trend: trend > 0 ? `+${trend.toFixed(1)}%` : `${trend.toFixed(1)}%`,
      trendDirection: trend >= 0 ? 'up' : 'down'
    };
  };

  // Calculate deck performance for TCG (use unfiltered matches for analysis)
  const calculateDeckPerformance = () => {
    if (activeTab !== 'TCG') return null;

    const deckStats = {};
    const deckMatchups = {}; // Track matchups for each deck
    let activeMatches = allMatches.filter(match => match.gameType === 'TCG');
    
    // Filter by current season if available
    if (currentSeason) {
      activeMatches = activeMatches.filter(match => {
        const matchDate = new Date(match.createdAt);
        return matchDate >= new Date(currentSeason.startDate) && 
               matchDate <= new Date(currentSeason.endDate);
      });
    }
    
    // If no matches in current season but we have matches overall, show all matches
    // Do not fallback to all-time when in a season view; show 0 for this season

    activeMatches.forEach(match => {
      const deckName = match.deckName || getDeckName(match.activeDeck) || 'Unknown Deck';
      if (!deckStats[deckName]) {
        deckStats[deckName] = {
          name: deckName,
          matches: 0,
          wins: 0,
          losses: 0,
          uniqueOpponents: new Set(),
          recentMatches: [],
          sprite: match.deckSprite?.url || null,
          secondarySprite: match.secondaryDeckSprite?.url || null
        };
        deckMatchups[deckName] = {};
      }

      deckStats[deckName].matches++;
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      const isWin = matchWins > matchLosses;
      
      if (isWin) {
        deckStats[deckName].wins++;
      } else {
        deckStats[deckName].losses++;
      }

      // Track matchups
      if (match.opponentDeck) {
        deckStats[deckName].uniqueOpponents.add(match.opponentDeck);
        
        if (!deckMatchups[deckName][match.opponentDeck]) {
          deckMatchups[deckName][match.opponentDeck] = { wins: 0, losses: 0 };
        }
        
        if (isWin) {
          deckMatchups[deckName][match.opponentDeck].wins++;
        } else {
          deckMatchups[deckName][match.opponentDeck].losses++;
        }
      }

      // Track recent matches (up to 10)
      if (deckStats[deckName].recentMatches.length < 10) {
        deckStats[deckName].recentMatches.push(isWin ? 'win' : 'loss');
      }
    });

    // Calculate matchup win rates and find best/worst
    Object.keys(deckStats).forEach(deckName => {
      const matchups = deckMatchups[deckName];
      let bestMatchup = null;
      let worstMatchup = null;
      let bestWinRate = -1;
      let worstWinRate = 101;
      let bestMatchupStats = null;
      let worstMatchupStats = null;

      Object.entries(matchups).forEach(([opponentDeck, stats]) => {
        const totalGames = stats.wins + stats.losses;
        if (totalGames > 0) {
          const winRate = (stats.wins / totalGames) * 100;
          
          if (winRate > bestWinRate) {
            bestWinRate = winRate;
            bestMatchup = opponentDeck;
            bestMatchupStats = { wins: stats.wins, losses: stats.losses, winRate };
          }
          
          if (winRate < worstWinRate) {
            worstWinRate = winRate;
            worstMatchup = opponentDeck;
            worstMatchupStats = { wins: stats.wins, losses: stats.losses, winRate };
          }
        }
      });

      deckStats[deckName].bestMatchup = bestMatchup;
      deckStats[deckName].worstMatchup = worstMatchup;
      deckStats[deckName].bestMatchupStats = bestMatchupStats;
      deckStats[deckName].worstMatchupStats = worstMatchupStats;
    });

    // Calculate recent performance trend
    Object.values(deckStats).forEach(deck => {
      const recentWins = deck.recentMatches.filter(r => r === 'win').length;
      const recentWinRate = deck.recentMatches.length > 0 ? 
        (recentWins / deck.recentMatches.length) * 100 : 0;
      
      // Compare to overall win rate to determine trend
      const overallWinRate = deck.matches > 0 ? (deck.wins / deck.matches) * 100 : 0;
      deck.recentWinRate = recentWinRate.toFixed(1);
      deck.trend = recentWinRate > overallWinRate ? 'up' : recentWinRate < overallWinRate ? 'down' : 'stable';
    });

    // Get current deck (most recently used)
    const currentDeckName = activeMatches.length > 0 ? 
      (activeMatches[0].deckName || getDeckName(activeMatches[0].activeDeck) || 'Unknown Deck') : null;
    const currentDeck = currentDeckName ? deckStats[currentDeckName] : null;

    return {
      currentDeck: currentDeck ? {
        ...currentDeck,
        winRate: currentDeck.matches > 0 ? ((currentDeck.wins / currentDeck.matches) * 100).toFixed(1) : 0,
        uniqueOpponents: currentDeck.uniqueOpponents.size
      } : null
    };
  };

  // Calculate team performance for VGC (use unfiltered matches for analysis)
  const calculateTeamPerformance = () => {
    if (activeTab !== 'VGC') return null;

    let activeMatches = allMatches.filter(match => match.gameType === 'VGC');
    const totalVGCMatches = activeMatches.length;
    
    // Filter by current season if available
    if (currentSeason) {
      activeMatches = activeMatches.filter(match => {
        const matchDate = new Date(match.createdAt);
        return matchDate >= new Date(currentSeason.startDate) && 
               matchDate <= new Date(currentSeason.endDate);
      });
    }
    
    // Do not fallback to all-time when in a season view; show 0 for this season

    // If no matches, check if there are any teams
    if (activeMatches.length === 0) {
      const vgcTeams = teams?.filter(team => {
        return team.format?.toUpperCase() === 'VGC' || team.isVGCTeam === true;
      }) || [];
      if (vgcTeams.length === 0) {
        return { noTeamsOrMatches: true };
      } else {
        return { hasTeamsNoMatches: true, teamCount: vgcTeams.length };
      }
    }

    // Find most common team composition
    const teamCompositions = {};


    // Helper function to normalize team names
    const normalizeTeamName = (name) => name.toLowerCase().trim().split('-')[0];
    
    activeMatches.forEach(match => {
      // Normalize team to sorted string for comparison
      const team = match.playerTeam || [];
      const teamArray = Array.isArray(team) ? team : (typeof team === 'string' ? team.split(',') : []);
      
      // Create normalized key for comparison
      const teamKey = teamArray
        .map(normalizeTeamName)
        .sort()
        .join(',');
      
      if (!teamCompositions[teamKey]) {
        // Try to find a matching team in the user's teams to get the name
        let teamName = null;
        let teamId = null;
        
        if (teams && teams.length > 0) {
          const matchingTeam = teams.find(t => {
            if (!t.pokemon || t.pokemon.length === 0) return false;
            const savedTeamKey = t.pokemon
              .map(p => normalizeTeamName(p.name))
              .sort()
              .join(',');
            return savedTeamKey === teamKey;
          });
          
          if (matchingTeam) {
            teamName = matchingTeam.name;
            teamId = matchingTeam._id;
          }
        }
        
        teamCompositions[teamKey] = {
          team: teamArray, // Use the array form
          teamName: teamName,
          teamId: teamId,
          matches: 0,
          wins: 0,
          losses: 0,
          uniqueOpponents: new Set(),
          recentMatches: [],
          leadPairs: {}
        };
      }

      teamCompositions[teamKey].matches++;
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      const isWin = matchWins > matchLosses;
      
      if (isWin) {
        teamCompositions[teamKey].wins++;
      } else {
        teamCompositions[teamKey].losses++;
      }

      // Track unique opponent teams
      if (match.opponentTeam) {
        const opponentArray = Array.isArray(match.opponentTeam) 
          ? match.opponentTeam 
          : (typeof match.opponentTeam === 'string' ? match.opponentTeam.split(',') : []);
        const opponentKey = opponentArray
          .map(normalizeTeamName)
          .sort()
          .join(',');
        teamCompositions[teamKey].uniqueOpponents.add(opponentKey);
      }

      // Track lead pairs for each game
      match.games?.forEach(game => {
        if (game.leadPokemon && game.leadPokemon.length >= 2) {
          const leadPair = game.leadPokemon.slice(0, 2).sort().join(' + ');
          if (!teamCompositions[teamKey].leadPairs[leadPair]) {
            teamCompositions[teamKey].leadPairs[leadPair] = 0;
          }
          teamCompositions[teamKey].leadPairs[leadPair]++;
        }
      });

      // Track recent matches
      if (teamCompositions[teamKey].recentMatches.length < 10) {
        teamCompositions[teamKey].recentMatches.push(isWin ? 'win' : 'loss');
      }
    });


    // Find most used team
    let mostUsedTeam = null;
    let maxMatches = 0;

    Object.values(teamCompositions).forEach(teamData => {
      if (teamData.matches > maxMatches) {
        maxMatches = teamData.matches;
        mostUsedTeam = teamData;
      }
    });

    if (!mostUsedTeam) {
      return null;
    }

    // Find most common lead pair
    let mostCommonLeadPair = null;
    let maxLeadCount = 0;

    Object.entries(mostUsedTeam.leadPairs).forEach(([pair, count]) => {
      if (count > maxLeadCount) {
        maxLeadCount = count;
        mostCommonLeadPair = pair;
      }
    });

    // Calculate recent performance trend
    const recentWins = mostUsedTeam.recentMatches.filter(r => r === 'win').length;
    const recentWinRate = mostUsedTeam.recentMatches.length > 0 ? 
      (recentWins / mostUsedTeam.recentMatches.length) * 100 : 0;
    const overallWinRate = mostUsedTeam.matches > 0 ? 
      (mostUsedTeam.wins / mostUsedTeam.matches) * 100 : 0;

    const result = {
      mostUsedTeam: {
        ...mostUsedTeam,
        winRate: overallWinRate.toFixed(1),
        recentWinRate: recentWinRate.toFixed(1),
        trend: recentWinRate > overallWinRate ? 'up' : recentWinRate < overallWinRate ? 'down' : 'stable',
        uniqueOpponentsCount: mostUsedTeam.uniqueOpponents.size,
        mostCommonLeadPair: mostCommonLeadPair
      }
    };

    return result;
  };

  const performanceMetrics = calculatePerformanceMetrics();
  const deckPerformance = calculateDeckPerformance();
  const teamPerformance = calculateTeamPerformance();

  // Calculate all-time performance metrics (without season filter)
  const calculateAllTimePerformanceMetrics = () => {
    const activeMatches = allMatches.filter(match => match.gameType === activeTab);
    
    if (activeMatches.length === 0) return null;

    const wins = activeMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length;

    const losses = activeMatches.length - wins;
    const winRate = ((wins / activeMatches.length) * 100).toFixed(1);

    // Calculate trend (last 10 matches vs previous 10)
    const recentMatches = activeMatches.slice(0, 10);
    const previousMatches = activeMatches.slice(10, 20);
    
    const recentWins = recentMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length;

    const previousWins = previousMatches.length > 0 ? previousMatches.filter(match => {
      const matchWins = match.games?.filter(g => g.result === 'win')?.length || 0;
      const matchLosses = match.games?.filter(g => g.result === 'loss')?.length || 0;
      return matchWins > matchLosses;
    }).length : 0;

    const recentWinRate = recentMatches.length > 0 ? (recentWins / recentMatches.length) * 100 : 0;
    const previousWinRate = previousMatches.length > 0 ? (previousWins / previousMatches.length) * 100 : 0;
    const trend = recentWinRate - previousWinRate;

    return {
      totalMatches: activeMatches.length,
      wins,
      losses,
      winRate,
      recentWinRate: Math.round(recentWinRate),
      recentCount: recentMatches.length,
      trend: trend > 0 ? `+${trend.toFixed(1)}%` : `${trend.toFixed(1)}%`,
      trendDirection: trend >= 0 ? 'up' : 'down'
    };
  };

  const allTimePerformanceMetrics = calculateAllTimePerformanceMetrics();


  // Consider loading state of all data sources
  if (loading) {
    return (
      <div className="loader">
        <div className="loader-spinner" />
      </div>
    );
  }

  if (useModernUI) {
    return (
      <div className="dashboard-modern-container">
        <div className="dashboard-modern-layout">
          {/* Performance Analysis Panel */}
          <div className={`performance-panel${analyticsCollapsed ? ' is-collapsed' : ''}`}>
            <button
              type="button"
              className="performance-panel-header"
              onClick={() => setAnalyticsCollapsed(prev => !prev)}
              aria-expanded={!analyticsCollapsed}
              aria-controls="performance-panel-body"
            >
              <span className="performance-panel-title">
                <i className="fas fa-chart-line"></i>
                Performance Analysis
              </span>
              <span className="performance-panel-toggle">
                <span className="performance-panel-hint">Tap to {analyticsCollapsed ? 'expand' : 'collapse'}</span>
                <i className="fas fa-chevron-down performance-panel-chevron"></i>
              </span>
            </button>

            <div className="performance-panel__body" id="performance-panel-body">
            {performanceMetrics ? (
              <>
                {/* Compact KPI tile row (season/all-time toggle on the win-rate tile) */}
                {(() => {
                  const pm = (showAllTimeStats && allTimePerformanceMetrics) ? allTimePerformanceMetrics : performanceMetrics;
                  const scopeLabel = showAllTimeStats ? 'All-Time' : (currentSeason ? currentSeason.name : 'All-Time');
                  return (
                    <div className="dashboard-kpi-grid">
                      <button
                        type="button"
                        className="kpi-tile kpi-tile--clickable kpi-tile--winrate"
                        onClick={() => setShowAllTimeStats(!showAllTimeStats)}
                        title={currentSeason ? 'Toggle season / all-time' : undefined}
                      >
                        <span className="kpi-tile__main">
                          <span className="kpi-tile__label">{scopeLabel} Win Rate</span>
                          <span className="kpi-tile__value">{pm.winRate}%</span>
                          {currentSeason && (
                            <span className="kpi-tile__sub">
                              <i className="fas fa-sync-alt"></i>
                              {showAllTimeStats ? 'Showing all-time' : 'Click for all-time'}
                            </span>
                          )}
                        </span>
                        <span className={`stat-trend ${pm.trendDirection}`}>
                          <i className={`fas fa-arrow-${pm.trendDirection}`}></i>
                          {pm.trend}
                        </span>
                      </button>

                      <div className="kpi-tile">
                        <span className="kpi-tile__label">{scopeLabel} Record</span>
                        <span className="kpi-tile__value">{pm.wins}W – {pm.losses}L</span>
                      </div>

                      <div className="kpi-tile">
                        <span className="kpi-tile__label">Matches</span>
                        <span className="kpi-tile__value">{pm.totalMatches}</span>
                      </div>

                      <div className="kpi-tile">
                        <span className="kpi-tile__label">W : L</span>
                        <span className="kpi-tile__value">{pm.wins} - {pm.losses}</span>
                      </div>

                      {pm.recentCount > 0 && (
                        <div className="kpi-tile kpi-tile--wide">
                          <span className="kpi-tile__label">Recent Form · Last {pm.recentCount}</span>
                          <span className="kpi-tile__value kpi-tile__value--row">
                            {pm.recentWinRate}%
                            <span className={`stat-trend ${pm.trendDirection}`}>
                              <i className={`fas fa-arrow-${pm.trendDirection}`}></i>
                              {pm.trend}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <button
                  className="view-analytics-btn"
                  onClick={() => navigate(activeTab === 'TCG' ? '/analytics/tcg/matches' : '/analytics/vgc/matches')}
                >
                  <i className="fas fa-chart-bar"></i>
                  View All Analytics
                </button>

                {/* Deck Performance (TCG only) */}
                {activeTab === 'TCG' && (
                  deckPerformance && deckPerformance.currentDeck ? (
                  <div className="deck-performance-section">
                    <h4 className="deck-performance-title">Active Deck Performance</h4>
                    <div className="deck-card">
                      <div className="deck-card-header">
                        <div className="deck-name-row">
                          {deckPerformance.currentDeck.sprite && (
                            <div className="deck-sprites-stacked">
                              <img src={deckPerformance.currentDeck.sprite} alt="" className="deck-sprite-main" />
                              {deckPerformance.currentDeck.secondarySprite && (
                                <img src={deckPerformance.currentDeck.secondarySprite} alt="" className="deck-sprite-secondary" />
                              )}
                            </div>
                          )}
                          <span className="deck-name-full">{deckPerformance.currentDeck.name}</span>
                        </div>
                        <div className="deck-winrate-bubble">
                          <span className="winrate-text">{deckPerformance.currentDeck.winRate}%</span>
                        </div>
                      </div>
                      
                      <div className="deck-stats-grid">
                        <div className="deck-stat">
                          <span className="deck-stat-label">Record</span>
                          <span className="deck-stat-value">
                            {deckPerformance.currentDeck.wins}W - {deckPerformance.currentDeck.losses}L
                          </span>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Total Matches</span>
                          <span className="deck-stat-value">{deckPerformance.currentDeck.matches}</span>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Worst vs</span>
                          <div className="deck-stat-matchup">
                            <span className="deck-stat-value small">
                              {deckPerformance.currentDeck.worstMatchup || 'N/A'}
                            </span>
                            {deckPerformance.currentDeck.worstMatchupStats && (
                              <span className="matchup-winrate worst">
                                {deckPerformance.currentDeck.worstMatchupStats.winRate.toFixed(0)}%
                                <span className="matchup-record">
                                  ({deckPerformance.currentDeck.worstMatchupStats.wins}-{deckPerformance.currentDeck.worstMatchupStats.losses})
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Best vs</span>
                          <div className="deck-stat-matchup">
                            <span className="deck-stat-value small">
                              {deckPerformance.currentDeck.bestMatchup || 'N/A'}
                            </span>
                            {deckPerformance.currentDeck.bestMatchupStats && (
                              <span className="matchup-winrate best">
                                {deckPerformance.currentDeck.bestMatchupStats.winRate.toFixed(0)}%
                                <span className="matchup-record">
                                  ({deckPerformance.currentDeck.bestMatchupStats.wins}-{deckPerformance.currentDeck.bestMatchupStats.losses})
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="deck-recent-trend">
                        <span className="trend-label">Recent Performance (Last 10):</span>
                        <div className="trend-info">
                          <span className="trend-percentage">{deckPerformance.currentDeck.recentWinRate}%</span>
                          <span className={`trend-arrow ${deckPerformance.currentDeck.trend}`}>
                            {deckPerformance.currentDeck.trend === 'up' && <i className="fas fa-arrow-up"></i>}
                            {deckPerformance.currentDeck.trend === 'down' && <i className="fas fa-arrow-down"></i>}
                            {deckPerformance.currentDeck.trend === 'stable' && <i className="fas fa-minus"></i>}
                          </span>
                        </div>
                      </div>
                      
                      <div className="deck-actions">
                        <button 
                          className="deck-action-btn full-width"
                          onClick={() => navigate('/analytics/tcg/matches')}
                        >
                          <i className="fas fa-chart-bar"></i>
                          View Match Analytics
                        </button>
                      </div>
                    </div>
                  </div>
                  ) : (
                    <div className="deck-performance-section">
                      <h4 className="deck-performance-title">Active Deck Performance</h4>
                      <div className="deck-card">
                        <div className="no-deck-selected">
                          <i className="fas fa-layer-group no-deck-icon"></i>
                          <p className="no-deck-text">No deck currently selected</p>
                          <button 
                            className="deck-action-btn full-width"
                            onClick={() => navigate('/decks')}
                          >
                            <i className="fas fa-list"></i>
                            View Deck List
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* Team Performance (VGC only) */}
                {activeTab === 'VGC' && (
                  teamPerformance?.noTeamsOrMatches ? (
                    <div className="deck-performance-section">
                      <h4 className="deck-performance-title">Team Performance</h4>
                      <div className="deck-card">
                        <div className="no-deck-selected">
                          <i className="fas fa-users no-deck-icon"></i>
                          <p className="no-deck-text">No teams currently registered</p>
                          <button 
                            className="deck-action-btn full-width"
                            onClick={() => navigate('/log-match/vgc')}
                          >
                            <i className="fas fa-plus"></i>
                            Log New Match
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : teamPerformance?.hasTeamsNoMatches ? (
                    <div className="deck-performance-section">
                      <h4 className="deck-performance-title">Team Performance</h4>
                      <div className="deck-card">
                        <div className="no-deck-selected">
                          <i className="fas fa-chart-line no-deck-icon"></i>
                          <p className="no-deck-text">No matches logged yet</p>
                          <p className="no-deck-subtext" style={{ fontSize: '14px', color: '#9e9e9e', marginBottom: '20px' }}>
                            You have {teamPerformance.teamCount} team{teamPerformance.teamCount !== 1 ? 's' : ''} saved
                          </p>
                          <button 
                            className="deck-action-btn full-width"
                            onClick={() => navigate('/log-match/vgc')}
                          >
                            <i className="fas fa-plus"></i>
                            Log Your First Match
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : teamPerformance?.mostUsedTeam ? (
                  <div className="deck-performance-section">
                    <h4 className="deck-performance-title">Most Used Team</h4>
                    <div className="deck-card">
                      <div className="deck-card-header">
                        <div className="deck-name-row">
                          <TeamNameWithTooltip 
                            teamName={teamPerformance.mostUsedTeam.teamName}
                            team={teamPerformance.mostUsedTeam.team}
                            generateName={generatePokemonThemedName}
                          />
                        </div>
                        <div className="deck-winrate-bubble">
                          <span className="winrate-text">{teamPerformance.mostUsedTeam.winRate}%</span>
                        </div>
                      </div>
                      
                      <div className="deck-stats-grid">
                        <div className="deck-stat">
                          <span className="deck-stat-label">Record</span>
                          <span className="deck-stat-value">
                            {teamPerformance.mostUsedTeam.wins}-{teamPerformance.mostUsedTeam.losses}
                          </span>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Total Matches</span>
                          <span className="deck-stat-value">{teamPerformance.mostUsedTeam.matches}</span>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Common Lead</span>
                          <div className="deck-stat-value small">
                            {teamPerformance.mostUsedTeam.mostCommonLeadPair ? (
                              <div className="lead-pair-sprites">
                                {teamPerformance.mostUsedTeam.mostCommonLeadPair.split(' + ').map((pokemon) => (
                                  <PokemonSprite 
                                    key={pokemon}
                                    pokemonName={pokemon.trim()}
                                    size="small"
                                    className="lead-sprite"
                                    title={pokemon.trim()}
                                  />
                                ))}
                              </div>
                            ) : 'N/A'}
                          </div>
                        </div>
                        <div className="deck-stat">
                          <span className="deck-stat-label">Unique Teams</span>
                          <span className="deck-stat-value">{teamPerformance.mostUsedTeam.uniqueOpponentsCount}</span>
                        </div>
                      </div>
                      
                      <div className="deck-recent-trend">
                        <span className="trend-label">Recent Performance (Last 10):</span>
                        <div className="trend-info">
                          <span className="trend-percentage">{teamPerformance.mostUsedTeam.recentWinRate}%</span>
                          <span className={`trend-arrow ${teamPerformance.mostUsedTeam.trend}`}>
                            {teamPerformance.mostUsedTeam.trend === 'up' && <i className="fas fa-arrow-up"></i>}
                            {teamPerformance.mostUsedTeam.trend === 'down' && <i className="fas fa-arrow-down"></i>}
                            {teamPerformance.mostUsedTeam.trend === 'stable' && <i className="fas fa-minus"></i>}
                          </span>
                        </div>
                      </div>
                      
                      <div className="deck-actions">
                        <button 
                          className="deck-action-btn full-width"
                          onClick={() => navigate('/analytics/vgc/matches')}
                        >
                          <i className="fas fa-chart-bar"></i>
                          View Match Analytics
                        </button>
                      </div>
                    </div>
                  </div>
                  ) : null
                )}

              </>
            ) : (
              <div className="empty-state-text">No matches recorded yet</div>
            )}

            {/* Global logging stats live inside the analytics rail */}
            <MatchCounterTicker />
            </div>
          </div>

          {/* Main Content Area */}
          <div className="dashboard-main-content">
            {/* Modern Header */}
            <div className="dashboard-modern-header">
              <div className="dashboard-header-content">
                <div className="dashboard-title-section">
                  <h1>Your Match History</h1>
                  <p className="dashboard-subtitle">Track Every Match, Master Every Meta.</p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className={`dashboard-navigation-container ${!showBothGames ? 'single-game' : ''}`}>
              {showBothGames ? (
                <>
                  <div className="dashboard-tabs-modern">
                    <button
                      className={`tab-button-modern ${activeTab === 'TCG' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('TCG');
                        localStorage.setItem('dashboardActiveTab', 'TCG');
                      }}
                    >
                      TCG Matches
                    </button>
                    <button
                      className={`tab-button-modern ${activeTab === 'VGC' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('VGC');
                        localStorage.setItem('dashboardActiveTab', 'VGC');
                      }}
                    >
                      VGC Matches
                    </button>
                  </div>
                  <div className="dashboard-tabs-modern">
                    <button
                      className={`tab-button-modern ${dashboardView === 'matches' ? 'active' : ''}`}
                      onClick={() => {
                        setDashboardView('matches');
                        localStorage.setItem('dashboardView', 'matches');
                      }}
                    >
                      <i className="fas fa-list"></i>
                      Matches
                    </button>
                    <button
                      className={`tab-button-modern ${dashboardView === 'groups' ? 'active' : ''}`}
                      onClick={() => {
                        setDashboardView('groups');
                        localStorage.setItem('dashboardView', 'groups');
                      }}
                    >
                      <i className="fas fa-trophy"></i>
                      Tournaments
                    </button>
                  </div>
                </>
              ) : (
                /* Single game mode - only show Matches/Groups toggle, same styling as TCG/VGC tabs */
                <div className="dashboard-tabs-modern centered-toggle">
                  <button
                    className={`tab-button-modern ${dashboardView === 'matches' ? 'active' : ''}`}
                    onClick={() => {
                      setDashboardView('matches');
                      localStorage.setItem('dashboardView', 'matches');
                    }}
                  >
                    <i className="fas fa-list"></i>
                    Matches
                  </button>
                  <button
                    className={`tab-button-modern ${dashboardView === 'groups' ? 'active' : ''}`}
                    onClick={() => {
                      setDashboardView('groups');
                      localStorage.setItem('dashboardView', 'groups');
                    }}
                  >
                    <i className="fas fa-object-group"></i>
                    Groups
                  </button>
                </div>
              )}
            </div>

            {dashboardView === 'matches' ? (
              <>
                {/* Team Filter Banner */}
                {teamFilter && activeTab === 'VGC' && (
                  <div className="team-filter-banner" style={{
                    backgroundColor: '#e3f2fd',
                    padding: '10px 20px',
                    marginBottom: '15px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <i className="fas fa-filter" style={{ marginRight: '8px', color: '#1976d2' }}></i>
                      Showing matches for team: <strong>{teamFilterName || 'Unnamed Team'}</strong>
                    </div>
                    <button
                      onClick={() => {
                        setTeamFilter(null);
                        setTeamFilterName(null);
                      }}
                      className="btn btn-sm"
                      style={{ padding: '4px 12px' }}
                    >
                      <i className="fas fa-times"></i> Clear Filter
                    </button>
                  </div>
                )}
                
                {/* Controls and Filters */}
                <div className="dashboard-controls toolbar">
                  <div className="search-filter-group toolbar__group">
                    <input
                      type="text"
                      placeholder="Search matches..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="search-input-modern"
                    />
                    <select
                      value={sortOption}
                      onChange={e => setSortOption(e.target.value)}
                      className="filter-select-modern"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="a-z">{activeTab === 'VGC' ? 'Opponent Name' : 'Deck Name'} (A-Z)</option>
                      <option value="z-a">{activeTab === 'VGC' ? 'Opponent Name' : 'Deck Name'} (Z-A)</option>
                    </select>
                    {Array.isArray(availableTags) && availableTags.length > 0 && (
                      <select
                        multiple
                        value={selectedTags}
                        onChange={e => {
                          const options = Array.from(e.target.selectedOptions);
                          setSelectedTags(options.map(opt => opt.value));
                        }}
                        className="filter-select-modern tag-filter"
                        style={{ minHeight: '42px', maxHeight: '42px', overflowY: 'auto' }}
                      >
                        <option value="" disabled style={{ display: selectedTags.length === 0 ? 'block' : 'none' }}>
                          Filter by tags...
                        </option>
                        {availableTags.map(tag => (
                          <option key={tag.name} value={tag.name}>
                            {tag.name} ({tag.usageCount})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="action-buttons-group toolbar__actions">
                    <button onClick={handleLogMatchClick} className="action-button-primary">
                      <i className="fas fa-plus" />
                      Log New Match
                    </button>
                    <button onClick={handleCreateGroupClick} className="action-button-secondary">
                      <i className="fas fa-trophy" />
                      Create Tournament
                    </button>
                  </div>
                </div>

                {/* Active Tag Filters */}
                {selectedTags.length > 0 && (
                  <div className="active-tag-filters" style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#616161' }}>
                      <i className="fas fa-filter" style={{ marginRight: '6px' }}></i>
                      Active Filters:
                    </span>
                    {selectedTags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                      >
                        <i className="fas fa-tag" style={{ fontSize: '11px' }}></i>
                        {tag}
                        <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                      </span>
                    ))}
                    <button
                      onClick={() => setSelectedTags([])}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: 'transparent',
                        border: '1px solid #dc2626',
                        color: '#dc2626',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                )}

                {/* Match List */}
                {sortedMatches?.length > 0 ? (
                  <div className="dashboard-match-results">
                    <div className="dashboard-results-bar">
                      <span className="dashboard-results-count">
                        {sortedMatches.length} {sortedMatches.length === 1 ? 'match' : 'matches'}
                      </span>
                      <Pagination
                        currentPage={currentPage}
                        totalItems={sortedMatches.length}
                        itemsPerPage={matchesPerPage}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                    <div className="modern-match-list">
                      {Array.isArray(sortedMatches) && 
                        sortedMatches
                          .slice((currentPage - 1) * matchesPerPage, currentPage * matchesPerPage)
                          .map(match => (
                            <React.Fragment key={match._id}>
                              {match.gameType === 'VGC' ? (
                                <MatchItemVGC 
                                  match={match} 
                                  deleteMatch={deleteVGCMatch} 
                                />
                              ) : (
                                <MatchItemTCG match={match} deleteMatch={deleteTCGMatch} />
                              )}
                            </React.Fragment>
                          ))
                      }
                    </div>
                    
                    <Pagination
                      currentPage={currentPage}
                      totalItems={sortedMatches.length}
                      itemsPerPage={matchesPerPage}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                ) : (
                  <div className="empty-state-modern">
                    <div className="empty-state-icon">
                      <i className="fas fa-inbox"></i>
                    </div>
                    <h3 className="empty-state-title">No matches found</h3>
                    <p className="empty-state-text">Start logging matches to see them here</p>
                  </div>
                )}
              </>
            ) : (
              <MatchGroups />
            )}
          </div>
        </div>
        
        <MatchGroupModal
          isOpen={isGroupModalOpen}
          onClose={handleCloseGroupModal}
          defaultType={activeTab.toLowerCase()}
        />

        {showLogChooser && (
          <div className="modal-overlay" onClick={() => setShowLogChooser(false)}>
            <div className="modal-container log-chooser" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Log a Match</h2>
                <button className="modal-close-btn" onClick={() => setShowLogChooser(false)}>×</button>
              </div>
              <div className="modal-body log-chooser__options">
                <button
                  className="log-chooser__option"
                  onClick={() => { setShowLogChooser(false); navigate(activeTab === 'TCG' ? '/log-match/tcg' : '/log-match/vgc'); }}
                >
                  <span className="log-chooser__icon"><i className="fas fa-pen-to-square" /></span>
                  <span className="log-chooser__text">
                    <strong>Manual logging</strong>
                    <small>Enter the match details yourself</small>
                  </span>
                  <i className="fas fa-chevron-right log-chooser__arrow" />
                </button>
                {activeTab === 'TCG' && (
                  <>
                    <button
                      className="log-chooser__option"
                      onClick={() => { setShowLogChooser(false); navigate('/import-match'); }}
                    >
                      <span className="log-chooser__icon"><i className="fas fa-file-import" /></span>
                      <span className="log-chooser__text">
                        <strong>Import PTCGL</strong>
                        <small>Paste a PTCGL match log to auto-fill</small>
                      </span>
                      <i className="fas fa-chevron-right log-chooser__arrow" />
                    </button>
                    <button
                      className="log-chooser__option"
                      onClick={() => { setShowLogChooser(false); navigate('/import-limitless'); }}
                    >
                      <span className="log-chooser__icon"><i className="fas fa-trophy" /></span>
                      <span className="log-chooser__text">
                        <strong>Import Limitless Tournament</strong>
                        <small>Pull your matches from a Limitless event</small>
                      </span>
                      <i className="fas fa-chevron-right log-chooser__arrow" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <AnnouncementModal
          isOpen={showAnnouncement}
          onClose={handleCloseAnnouncement}
        />

        <UsernamePromptModal
          isOpen={showUsernamePrompt}
          onClose={() => setShowUsernamePrompt(false)}
          onSave={handleSaveUsernames}
          onDismiss={handleDismissUsernamePrompt}
        />
      </div>
    );
  }

  // Original UI
  return (
    <div className="dashboard-page-container">
      <div className="dashboard-header-container">
        <div className="dashboard-header">
          <h2>Your Match History</h2>
          <p>Track Every Match, Master Every Meta.</p>
        </div>
        <MatchCounterTicker />
      </div>

      <div className="tabs">
        <div className="tabs-left">
          <button
            className={`tab-button ${activeTab === 'TCG' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('TCG');
              localStorage.setItem('dashboardActiveTab', 'TCG');
            }}
          >
            TCG Matches
          </button>
          <button
            className={`tab-button ${activeTab === 'VGC' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('VGC');
              localStorage.setItem('dashboardActiveTab', 'VGC');
            }}
          >
            VGC Matches
          </button>
        </div>
        <div className="tabs-right">
          <button
            className={`view-button ${dashboardView === 'matches' ? 'active' : ''}`}
            onClick={() => {
              // Check if we're already in this view to avoid unnecessary re-renders
              if (previousView.current !== 'matches') {
                setDashboardView('matches');
                localStorage.setItem('dashboardView', 'matches');
                previousView.current = 'matches';
              }
            }}
          >
            Matches
          </button>
          <button
            className={`view-button ${dashboardView === 'groups' ? 'active' : ''}`}
            onClick={() => {
              // Check if we're already in this view to avoid unnecessary re-renders
              if (previousView.current !== 'groups') {
                setDashboardView('groups');
                localStorage.setItem('dashboardView', 'groups');
                previousView.current = 'groups';
              }
            }}
          >
            Groups
          </button>
        </div>
      </div>

      <div className="dashboard-actions">
        <button onClick={handleLogMatchClick} className="btn btn-primary">
          <i className="fas fa-plus" /> Log New Match
        </button>
        {activeTab === 'TCG' && dashboardView === 'matches' && (
          <>
            <button onClick={() => navigate('/import-match')} className="btn btn-primary">
              <i className="fas fa-file-import" /> Import PTCGL Match
            </button>
            <button onClick={() => navigate('/import-limitless')} className="btn btn-primary">
              <i className="fas fa-trophy" /> Import from Limitless
            </button>
          </>
        )}
        {dashboardView === 'matches' && (
          <>
            <button onClick={handleCreateGroupClick} className="btn btn-secondary">
              <i className="fas fa-object-group" /> Create Group
            </button>
            {Array.isArray(sortedMatches) && sortedMatches.length > 0 && (
              <button
                className="btn btn-light"
                onClick={() =>
                  exportToCSV(
                    sortedMatches || [],
                    `pokemon_${activeTab.toLowerCase()}_matches_${new Date().toISOString().split('T')[0]}`,
                    activeTab.toLowerCase()
                  )
                }
              >
                <i className="fas fa-download" /> Export Data
              </button>
            )}
          </>
        )}
      </div>

      {dashboardView === 'matches' ? (
        <>
          <div className="match-filters">
            <input
              type="text"
              placeholder="Search matches..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="form-control"
            />
            <select
              value={sortOption}
              onChange={e => setSortOption(e.target.value)}
              className="form-control sort-dropdown"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="a-z">{activeTab === 'VGC' ? 'Opponent Name' : 'Deck Name'} (A-Z)</option>
              <option value="z-a">{activeTab === 'VGC' ? 'Opponent Name' : 'Deck Name'} (Z-A)</option>
            </select>
          </div>

          {sortedMatches?.length > 0 ? (
            <>
              <div className="match-list">
                {Array.isArray(sortedMatches) && 
                  sortedMatches
                    .slice((currentPage - 1) * matchesPerPage, currentPage * matchesPerPage)
                    .map(match => (
                      <React.Fragment key={match._id}>
                        {match.gameType === 'VGC' ? (
                          <MatchItemVGC 
                            match={match} 
                            deleteMatch={deleteVGCMatch} 
                          />
                        ) : (
                          <MatchItemTCG match={match} deleteMatch={deleteTCGMatch} />
                        )}
                      </React.Fragment>
                    ))
                }
              </div>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={sortedMatches.length}
                itemsPerPage={matchesPerPage}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="no-matches">
              <h3>No matches found</h3>
            </div>
          )}
        </>
      ) : (
        dashboardView === 'groups' && <MatchGroups />
      )}
      
      <MatchGroupModal
        isOpen={isGroupModalOpen}
        onClose={handleCloseGroupModal}
        defaultType={activeTab.toLowerCase()}
      />

      <AnnouncementModal
        isOpen={showAnnouncement}
        onClose={handleCloseAnnouncement}
      />

      <UsernamePromptModal
        isOpen={showUsernamePrompt}
        onClose={() => setShowUsernamePrompt(false)}
        onSave={handleSaveUsernames}
        onDismiss={handleDismissUsernamePrompt}
      />
    </div>
  );
};

export default Dashboard;